import { Paperclip, Send, X } from 'lucide-react'
import Image from 'next/image'
import * as React from 'react'

import { RichTextarea, RichTextareaHandle } from '@/components/custom/rich-textarea'
import { Button } from '@/components/ui/button'
import { EmojiPicker } from '@/components/ui/emoji-picker'

import { ReplyPreview } from './reply-preview'

interface ChatInputProps {
  newMessage: string
  selectedFile: File | null
  isUploadingFile: boolean
  pendingTargetUserId: number | undefined
  isCreatingConversation: boolean
  isLoading: boolean
  selectedConversationId: number | undefined
  targetUserName: string
  replyingToMessage: {
    id: number
    content: string
    sender: {
      id: number
      firstName: string
      lastName: string
      avatar?: string
    }
    type: string
    fileUrl?: string
    fileName?: string
  } | null
  onMessageChange: (message: string) => void
  onSendMessage: () => void
  onFileSelect: (file?: File) => void
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onRemoveFile: () => void
  onTyping: (html: string) => void
  onInputFocus: () => void
  onKeyPress: (e: React.KeyboardEvent<HTMLDivElement>) => void
  onCancelReply: () => void
  insertEmoji: (emoji: string) => void
  insertCustomEmoji: (emoji: { id: number; name: string; imageUrl: string }) => void
  fileInputRef: React.RefObject<HTMLInputElement>
  inputRef: React.RefObject<RichTextareaHandle>
  mentionableUsers: Array<{
    id: number
    firstName: string
    lastName: string
    avatar?: string
    profileId?: string
  }>
}

export const ChatInput: React.FC<ChatInputProps> = ({
  newMessage,
  selectedFile,
  isUploadingFile,
  pendingTargetUserId,
  isCreatingConversation,
  isLoading,
  selectedConversationId,
  targetUserName,
  replyingToMessage,
  onMessageChange,
  onSendMessage,
  onFileSelect,
  onFileChange,
  onRemoveFile,
  onTyping,
  onInputFocus,
  onKeyPress,
  onCancelReply,
  insertEmoji,
  insertCustomEmoji,
  fileInputRef,
  inputRef,
  mentionableUsers,
}) => {
  const [mentionQuery, setMentionQuery] = React.useState<string | null>(null)
  const [selectedMentionIndex, setSelectedMentionIndex] = React.useState(0)

  const getPlainTextFromHtml = React.useCallback((html: string) => {
    if (typeof window === 'undefined') {
      return html.replace(/<[^>]*>/g, ' ')
    }
    const element = document.createElement('div')
    element.innerHTML = html
    return element.textContent || element.innerText || ''
  }, [])

  const filteredMentionUsers = React.useMemo(() => {
    if (mentionQuery === null) {
      return []
    }

    const keyword = mentionQuery.trim().toLowerCase()
    if (!keyword) {
      return mentionableUsers
    }

    return mentionableUsers.filter(user => {
      const fullName = `${user.firstName} ${user.lastName}`.toLowerCase()
      return (
        fullName.includes(keyword) ||
        user.firstName.toLowerCase().includes(keyword) ||
        user.lastName.toLowerCase().includes(keyword)
      )
    })
  }, [mentionQuery, mentionableUsers])

  const updateMessageValue = React.useCallback(
    (value: string) => {
      if (selectedFile) {
        onTyping(value)
      } else {
        onMessageChange(value)
      }
    },
    [onMessageChange, onTyping, selectedFile]
  )

  const handleEditorChange = React.useCallback(
    (value: string) => {
      updateMessageValue(value)
      const plainText = getPlainTextFromHtml(value)
      const match = plainText.match(/@([^\s@]*)$/)
      setMentionQuery(match ? match[1] : null)
    },
    [getPlainTextFromHtml, updateMessageValue]
  )

  const handleSelectMentionUser = React.useCallback(
    (user: { firstName: string; lastName: string; profileId?: string; id: number }) => {
      const fullName = `${user.firstName}\u00A0${user.lastName}`.trim()
      const profileLink = `/user/${user.profileId || user.id}`
      const mentionHtml = `<a href="${profileLink}" class="mention font-bold text-blue-700 hover:underline">@${fullName}</a> `
      const updatedMessage = newMessage.replace(/@([^\s@]*)$/, mentionHtml)
      updateMessageValue(updatedMessage)
      setMentionQuery(null)
      setSelectedMentionIndex(0)
      setTimeout(() => {
        if (inputRef.current?.focusAtEnd) {
          inputRef.current.focusAtEnd()
        } else {
          inputRef.current?.focus()
        }
      }, 0)
    },
    [inputRef, newMessage, updateMessageValue]
  )

  React.useEffect(() => {
    setSelectedMentionIndex(0)
  }, [mentionQuery])

  const handleInputKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (mentionQuery !== null && filteredMentionUsers.length > 0) {
        if (event.key === 'ArrowDown') {
          event.preventDefault()
          setSelectedMentionIndex(prev => (prev + 1) % filteredMentionUsers.length)
          return
        }

        if (event.key === 'ArrowUp') {
          event.preventDefault()
          setSelectedMentionIndex(prev => (prev - 1 + filteredMentionUsers.length) % filteredMentionUsers.length)
          return
        }

        if (event.key === 'Enter') {
          event.preventDefault()
          handleSelectMentionUser(filteredMentionUsers[selectedMentionIndex])
          return
        }

        if (event.key === 'Escape') {
          event.preventDefault()
          setMentionQuery(null)
          return
        }
      }
    },
    [filteredMentionUsers, handleSelectMentionUser, mentionQuery, selectedMentionIndex]
  )

  // Check if selected file is an image
  const isImageSelected = React.useMemo(
    () => !!(selectedFile && selectedFile.type.startsWith('image/')),
    [selectedFile]
  )

  // Create preview URL for image files
  const selectedFilePreviewUrl = React.useMemo(() => {
    if (!isImageSelected || !selectedFile) return undefined
    return URL.createObjectURL(selectedFile)
  }, [isImageSelected, selectedFile])

  // Cleanup preview URL on unmount
  React.useEffect(() => {
    return () => {
      if (selectedFilePreviewUrl) URL.revokeObjectURL(selectedFilePreviewUrl)
    }
  }, [selectedFilePreviewUrl])

  return (
    <div className="p-3 sm:p-4 border-t border-gray-200 bg-white">
      {/* Hidden file input */}
      <input ref={fileInputRef} type="file" className="hidden" onChange={onFileChange} accept="*/*" />

      {/* File preview section - positioned above main input for better balance */}
      {selectedFile && (
        <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-start gap-3">
            {isImageSelected && selectedFilePreviewUrl ? (
              <Image
                src={selectedFilePreviewUrl}
                alt={selectedFile.name}
                width={48}
                height={48}
                className="rounded-md object-cover flex-shrink-0"
              />
            ) : (
              <div className="h-12 w-12 bg-gray-100 rounded-md flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">📎</span>
              </div>
            )}

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{selectedFile.name}</div>
                  <div className="text-xs text-gray-500">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</div>
                </div>
                <Button variant="ghost" size="sm" onClick={onRemoveFile} className="h-7 w-7 p-0 hover:bg-gray-200">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reply preview section */}
      {replyingToMessage && (
        <div className="mb-3">
          <ReplyPreview replyingToMessage={replyingToMessage} onCancelReply={onCancelReply} />
        </div>
      )}

      {/* Main input controls section */}
      <div className="flex items-end gap-2">
        {/* Main text input */}
        <div className="flex-1 relative">
          <RichTextarea
            ref={inputRef}
            value={newMessage}
            onChange={handleEditorChange}
            onKeyDown={handleInputKeyDown}
            onKeyPress={onKeyPress}
            onFocus={onInputFocus}
            onImagePaste={onFileSelect}
            placeholder={
              selectedFile
                ? 'Add a message (optional)...'
                : !selectedConversationId
                  ? pendingTargetUserId
                    ? `Type a message to start chatting with ${targetUserName?.trim() || `User ${pendingTargetUserId}`}...`
                    : 'Type a message to start a new conversation...'
                  : 'Type your message...'
            }
            maxLength={500}
            disabled={isUploadingFile || !!(pendingTargetUserId && (isCreatingConversation || isLoading))}
            className={`w-full resize-none text-sm border-gray-200 focus:border-blue-500 ${
              selectedFile ? 'min-h-[32px] max-h-[100px]' : 'min-h-[40px] max-h-[120px]'
            }`}
          />

          {mentionQuery !== null && filteredMentionUsers.length > 0 && (
            <div className="absolute bottom-full mb-2 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-20">
              {filteredMentionUsers.map(user => (
                <button
                  key={user.id}
                  type="button"
                  className={`w-full text-left px-3 py-2 text-sm ${
                    filteredMentionUsers[selectedMentionIndex]?.id === user.id ? 'bg-gray-100' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => handleSelectMentionUser(user)}
                >
                  <span className="font-medium">
                    {user.firstName} {user.lastName}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Control buttons - aligned to bottom for better visual balance */}
        <div className="flex items-center gap-1">
          <EmojiPicker
            onEmojiSelect={insertEmoji}
            onCustomEmojiSelect={insertCustomEmoji}
            buttonIconClassName="!size-4"
            buttonClassName="!size-9 rounded-full hover:bg-gray-100"
          />

          <Button
            variant="ghost"
            size="sm"
            onClick={() => onFileSelect()}
            disabled={isUploadingFile}
            className="h-9 w-9 p-0 hover:bg-gray-100"
          >
            <Paperclip className="h-4 w-4" />
          </Button>

          <Button
            onClick={onSendMessage}
            disabled={
              (!newMessage.trim() && !selectedFile) ||
              !!(pendingTargetUserId && (isCreatingConversation || isLoading)) ||
              isUploadingFile
            }
            size="sm"
            className="h-9 px-3 bg-red-600 hover:bg-red-700 disabled:bg-gray-300"
          >
            {isUploadingFile ? (
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Character count - positioned below for better balance */}
      <div className="flex justify-between items-center mt-2">
        <span className="text-xs text-gray-500">{newMessage.length}/500</span>
        {selectedFile && <span className="text-xs text-blue-600 font-medium">File attached</span>}
      </div>
    </div>
  )
}
